// Publisher management — PLATFORM_ADMIN only (plan §5, Phase 2). Create, rename,
// enable/disable, delete publishers, and manage each one's collections. The
// create-user / publishing publisher selectors read the same publishers, so
// they update automatically.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import { MoreHorizontal, Plus, X, Building2, FolderTree, Trash2, Search, ShieldCheck } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';
import { AppShell } from '@/components/AppShell';
import { cn } from '@/lib/utils';

interface AdminPublisher {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  counts: { members: number; documents: number; collections: number };
  publishers?: { id: string; displayName: string }[]; // the PEOPLE who publish for this category (optional — tolerate older API)
}

interface Collection {
  id: string;
  publisherId: string;
  slug: string;
  name: string;
  description: string | null;
  documentCount: number;
}

export function Publishers() {
  const { t } = useTranslation('publishers');
  const [publishers, setPublishers] = useState<AdminPublisher[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<AdminPublisher | null>(null);
  const [collectionsTarget, setCollectionsTarget] = useState<AdminPublisher | null>(null);
  const [adminsTarget, setAdminsTarget] = useState<AdminPublisher | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: AdminPublisher[] }>('/admin/publishers');
      setPublishers(res.data);
    } catch {
      toast.error(t('common:error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(p: AdminPublisher, body: Record<string, unknown>, okMsg: string) {
    try {
      await apiFetch(`/admin/publishers/${p.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      toast.success(okMsg);
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    }
  }

  async function remove(p: AdminPublisher) {
    if (!window.confirm(t('actions.confirmDelete', { name: p.name }))) return;
    try {
      await apiFetch(`/admin/publishers/${p.id}`, { method: 'DELETE' });
      toast.success(t('toast.deleted'));
      void load();
    } catch (err) {
      // 409 = publisher still has documents → show the localized guidance.
      toast.error(
        err instanceof ApiError && err.status === 409
          ? t('toast.hasDocuments')
          : err instanceof ApiError
            ? err.message
            : t('common:error'),
      );
    }
  }

  return (
    <AppShell>
      <div className="mb-10 animate-fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium tracking-tight gradient-text pb-1">{t('title')}</h1>
            <p className="text-sm font-light text-slate-500 mt-1">{t('subtitle')}</p>
          </div>
          <button type="button" onClick={() => setCreateOpen(true)} className="btn-gradient">
            <Plus className="w-4 h-4" />
            {t('create')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
        </div>
      ) : publishers.length === 0 ? (
        <div className="text-center py-24 animate-fade-up">
          <div className="inline-flex p-[2px] rounded-2xl bg-brand-gradient mb-5">
            <div className="p-4 rounded-2xl bg-white">
              <Building2 className="w-8 h-8 text-brand-purple" />
            </div>
          </div>
          <p className="text-sm font-light text-slate-500 max-w-sm mx-auto">{t('empty')}</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card overflow-x-auto custom-scrollbar animate-fade-up [animation-delay:0.08s]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left">
                <th className="px-5 py-3.5 text-xs font-bold text-slate-500">{t('cols.name')}</th>
                <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.slug')}</th>
                <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.status')}</th>
                <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.members')}</th>
                <th className="px-3 py-3.5 text-xs font-bold text-slate-500 text-right">{t('cols.documents')}</th>
                <th className="px-3 py-3.5 text-xs font-bold text-slate-500 text-right">{t('cols.collections')}</th>
                <th className="px-3 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {publishers.map((p) => (
                <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3.5 font-semibold text-ink">{p.name}</td>
                  <td className="px-3 py-3.5">
                    <code className="text-xs font-mono text-brand-purple/80 bg-brand-purple/5 px-1.5 py-0.5 rounded">{p.slug}</code>
                  </td>
                  <td className="px-3 py-3.5">
                    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', p.isActive ? 'text-emerald-600' : 'text-slate-400')}>
                      <span className={cn('w-1.5 h-1.5 rounded-full', p.isActive ? 'bg-emerald-500' : 'bg-slate-300')} />
                      {p.isActive ? t('active') : t('disabled')}
                    </span>
                  </td>
                  <td className="px-3 py-3.5">
                    {(p.publishers ?? []).length === 0 ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {(p.publishers ?? []).map((u) => (
                          <span
                            key={u.id}
                            className="inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-brand-purple/5 text-brand-purple/80"
                          >
                            {u.displayName}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3.5 text-right tabular-nums text-slate-500">{p.counts.documents}</td>
                  <td className="px-3 py-3.5 text-right tabular-nums text-slate-500">{p.counts.collections}</td>
                  <td className="px-3 py-3.5 text-right">
                    <Dropdown.Root>
                      <Dropdown.Trigger asChild>
                        <button type="button" title={t('actions.menu')} className="p-1.5 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </Dropdown.Trigger>
                      <Dropdown.Portal>
                        <Dropdown.Content align="end" sideOffset={4} className="z-50 min-w-[210px] rounded-xl bg-white border border-slate-200 shadow-xl p-1.5 animate-scale-in">
                          <Item onClick={() => setRenameTarget(p)}>{t('actions.rename')}</Item>
                          <Item onClick={() => setCollectionsTarget(p)}>{t('actions.manageCollections')}</Item>
                          <Item onClick={() => setAdminsTarget(p)}>{t('actions.manageAdmins')}</Item>
                          <Item onClick={() => void patch(p, { isActive: !p.isActive }, p.isActive ? t('toast.disabled') : t('toast.enabled'))}>
                            {p.isActive ? t('actions.disable') : t('actions.enable')}
                          </Item>
                          <Dropdown.Separator className="my-1 h-px bg-slate-100" />
                          <Item destructive onClick={() => void remove(p)}>{t('actions.delete')}</Item>
                        </Dropdown.Content>
                      </Dropdown.Portal>
                    </Dropdown.Root>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PublisherDialog
        mode={renameTarget ? 'rename' : createOpen ? 'create' : null}
        target={renameTarget}
        onClose={() => {
          setCreateOpen(false);
          setRenameTarget(null);
        }}
        onDone={() => void load()}
      />

      <CollectionsDialog publisher={collectionsTarget} onClose={() => setCollectionsTarget(null)} onChanged={() => void load()} />

      <ManageAdminsDialog publisher={adminsTarget} onClose={() => setAdminsTarget(null)} />
    </AppShell>
  );
}

function Item({ children, onClick, destructive = false }: { children: React.ReactNode; onClick: () => void; destructive?: boolean }) {
  return (
    <Dropdown.Item
      onSelect={onClick}
      className={cn(
        'px-3 py-2 rounded-lg text-sm cursor-pointer outline-none transition-colors',
        destructive ? 'text-rose-600 data-[highlighted]:bg-rose-50' : 'text-ink data-[highlighted]:bg-slate-50',
      )}
    >
      {children}
    </Dropdown.Item>
  );
}

// ── Create / rename dialog ────────────────────────────────────

function PublisherDialog({
  mode,
  target,
  onClose,
  onDone,
}: {
  mode: 'create' | 'rename' | null;
  target: AdminPublisher | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('publishers');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === 'rename' && target) {
      setName(target.name);
    } else if (mode === 'create') {
      setName('');
      setSlug('');
    }
  }, [mode, target]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving || !name.trim()) return;
    setSaving(true);
    try {
      if (mode === 'rename' && target) {
        await apiFetch(`/admin/publishers/${target.id}`, { method: 'PATCH', body: JSON.stringify({ name: name.trim() }) });
        toast.success(t('toast.updated'));
      } else {
        await apiFetch('/admin/publishers', {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), ...(slug.trim() ? { slug: slug.trim() } : {}) }),
        });
        toast.success(t('toast.created', { name: name.trim() }));
      }
      onClose();
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={mode !== null} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-5">
            <Dialog.Title className="text-base font-semibold text-ink">
              {mode === 'rename' ? t('dialog.renameTitle') : t('dialog.createTitle')}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.name')}</span>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t('dialog.namePlaceholder')} className="paper-input" autoFocus />
            </label>
            {mode === 'create' && (
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.slug')}</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="hc-contractor-reports"
                  className="paper-input font-mono"
                />
                <span className="block text-[11px] font-light text-slate-400 mt-1.5 leading-snug">{t('dialog.slugHint')}</span>
              </label>
            )}
            <div className="flex justify-end gap-3 pt-1">
              <Dialog.Close type="button" className="btn-quiet">
                {t('common:cancel')}
              </Dialog.Close>
              <button type="submit" disabled={saving || !name.trim()} className="btn-gradient">
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {mode === 'rename' ? (saving ? t('dialog.saving') : t('dialog.save')) : saving ? t('dialog.creating') : t('dialog.submit')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Collections dialog ────────────────────────────────────────

function CollectionsDialog({ publisher, onClose, onChanged }: { publisher: AdminPublisher | null; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('publishers');
  const [collections, setCollections] = useState<Collection[]>([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!publisher) return;
    try {
      const res = await apiFetch<{ data: Collection[] }>(`/pub/collections?publisherId=${publisher.id}`);
      setCollections(res.data);
    } catch {
      setCollections([]);
    }
  }, [publisher]);

  useEffect(() => {
    if (publisher) {
      setNewName('');
      void load();
    }
  }, [publisher, load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!publisher || busy || !newName.trim()) return;
    setBusy(true);
    try {
      await apiFetch('/pub/collections', { method: 'POST', body: JSON.stringify({ publisherId: publisher.id, name: newName.trim() }) });
      toast.success(t('toast.collectionCreated'));
      setNewName('');
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setBusy(false);
    }
  }

  async function del(c: Collection) {
    if (!window.confirm(t('collections.confirmDelete', { name: c.name }))) return;
    try {
      await apiFetch(`/pub/collections/${c.id}`, { method: 'DELETE' });
      toast.success(t('toast.collectionDeleted'));
      await load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    }
  }

  return (
    <Dialog.Root open={!!publisher} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-1">
            <Dialog.Title className="text-base font-semibold text-ink flex items-center gap-2">
              <FolderTree className="w-4 h-4 text-brand-purple" />
              {t('collections.title', { name: publisher?.name ?? '' })}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-xs font-light text-slate-500 mb-4">{t('collections.subtitle')}</Dialog.Description>

          <form onSubmit={(e) => void add(e)} className="flex gap-2 mb-4">
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('collections.namePlaceholder')} className="paper-input flex-1" />
            <button type="submit" disabled={busy || !newName.trim()} className="btn-gradient shrink-0">
              <Plus className="w-4 h-4" />
              {t('collections.add')}
            </button>
          </form>

          {collections.length === 0 ? (
            <p className="text-sm font-light text-slate-400 py-6 text-center">{t('collections.empty')}</p>
          ) : (
            <div className="space-y-1.5">
              {collections.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-slate-100 hover:bg-slate-50/60 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{c.slug === 'non-collection' ? t('common:uncategorized') : c.name}</p>
                    <p className="text-xs font-light text-slate-400">
                      <code className="font-mono">{c.slug}</code> · {t('collections.documentsCount', { count: c.documentCount })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void del(c)}
                    title={t('collections.delete')}
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end mt-5">
            <Dialog.Close className="btn-quiet">{t('collections.close')}</Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Manage-admins dialog (appoint publisher-admins — [A]-only) ──────
// Appoint/revoke who may publish for this publisher. The appointee may be ANY
// active user (platform-wide directory, via /pub/grantees).

function ManageAdminsDialog({ publisher, onClose }: { publisher: AdminPublisher | null; onClose: () => void }) {
  const { t } = useTranslation('publishers');
  const [admins, setAdmins] = useState<{ userId: string; displayName: string; email: string }[] | null>(null);
  const [users, setUsers] = useState<{ id: string; displayName: string; email: string; company: string | null }[] | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!publisher) return;
    try {
      const res = await apiFetch<{ data: { userId: string; displayName: string; email: string }[] }>(
        `/admin/publishers/${publisher.id}/members`,
      );
      setAdmins(res.data);
    } catch {
      setAdmins([]);
    }
  }, [publisher]);

  // Load the whole grantable directory once when the dialog opens, so every
  // user is visible without typing; the search box just filters this list.
  const loadUsers = useCallback(async () => {
    if (!publisher) return;
    try {
      const res = await apiFetch<{ data: { id: string; displayName: string; email: string; company: string | null }[] }>(
        '/pub/grantees?limit=500',
      );
      setUsers(res.data);
    } catch {
      setUsers([]);
    }
  }, [publisher]);

  useEffect(() => {
    if (publisher) {
      setSearch('');
      setAdmins(null);
      setUsers(null);
      void load();
      void loadUsers();
    }
  }, [publisher, load, loadUsers]);

  const adminIds = useMemo(() => new Set((admins ?? []).map((a) => a.userId)), [admins]);

  // Directory minus the already-appointed publishers, filtered by the search box.
  const candidates = useMemo(() => {
    const list = (users ?? []).filter((u) => !adminIds.has(u.id));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.company ?? '').toLowerCase().includes(q),
    );
  }, [users, adminIds, search]);

  async function add(userId: string) {
    if (!publisher || busy) return;
    setBusy(true);
    try {
      await apiFetch(`/admin/publishers/${publisher.id}/members`, { method: 'POST', body: JSON.stringify({ userId }) });
      toast.success(t('toast.adminAdded'));
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(userId: string) {
    if (!publisher) return;
    try {
      await apiFetch(`/admin/publishers/${publisher.id}/members/${userId}`, { method: 'DELETE' });
      toast.success(t('toast.adminRemoved'));
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    }
  }

  return (
    <Dialog.Root open={!!publisher} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-1">
            <Dialog.Title className="text-base font-semibold text-ink flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-brand-purple" />
              {t('admins.title', { name: publisher?.name ?? '' })}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-xs font-light text-slate-500 mb-4">{t('admins.subtitle')}</Dialog.Description>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admins.search')}
              className="paper-input !pl-9"
            />
          </div>
          <div className="mb-4 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto custom-scrollbar">
            {users === null ? (
              <div className="flex justify-center py-6">
                <div className="w-7 h-7 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-sm font-light text-slate-400 py-4 text-center">{t('admins.noResults')}</p>
            ) : (
              candidates.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => void add(r.id)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <p className="text-sm font-medium text-ink">{r.displayName}</p>
                  <p className="text-xs font-light text-slate-400">{r.email}</p>
                </button>
              ))
            )}
          </div>

          <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-purple mb-2">{t('admins.current')}</h3>
          {admins === null ? (
            <div className="flex justify-center py-6">
              <div className="w-7 h-7 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
            </div>
          ) : admins.length === 0 ? (
            <p className="text-sm font-light text-slate-400 py-4 text-center">{t('admins.none')}</p>
          ) : (
            <div className="space-y-1.5">
              {admins.map((a) => (
                <div key={a.userId} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-slate-100 hover:bg-slate-50/60 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{a.displayName}</p>
                    <p className="text-xs font-light text-slate-400 truncate">{a.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void remove(a.userId)}
                    title={t('admins.remove')}
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end mt-5">
            <Dialog.Close className="btn-quiet">{t('admins.close')}</Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
